<template>
  <div class="contenedor-principal">
    <h1>El Sapito Corredor (Bufo calamita): Un Tesoro Ecológico en Peligro</h1>
    <h2>Un anfibio fascinante y vulnerable en nuestros ecosistemas</h2>

    <p>En los prados y zonas húmedas de Europa, y muy cerca de nosotros en Bizkaia, podemos encontrar a un pequeño y
      encantador anfibio conocido popularmente como <strong>sapito corredor</strong> (científicamente <em>Epidalea
        calamita</em>, anteriormente clasificado como <em>Bufo calamita</em>). Es una especie fascinante que merece
      nuestra atención y protección. En este artículo, exploraremos la vida de este pequeño anfibio, su relevancia
      ecológica y los desafíos a los que se enfrenta en un mundo amenazado por la pérdida de hábitats.</p>

    <h2>Características: El maestro del camuflaje terrestre</h2>
    <p>El <strong>sapito corredor</strong> es nativo de gran parte de Europa. Se encuentra principalmente en prados,
      dunas costeras, canteras abandonadas y áreas cercanas a zonas encharcables. Aunque su tamaño varía entre 4 y 8
      centímetros, este pequeño anfibio tiene una apariencia inconfundible:</p>

    <ul>
      <li><strong>Piel y coloración:</strong> Su piel es rugosa y puede variar en tonos desde el marrón y gris hasta el
        verde oliva, a menudo con una distintiva línea amarilla brillante que recorre su espalda. Este diseño le
        proporciona un camuflaje excelente contra los depredadores.</li>
      <li><strong>Forma de desplazarse:</strong> A diferencia de las ranas, el sapito corredor tiene las patas traseras
        cortas. Por tanto, rara vez salta; su método de locomoción es <strong>correr rápidamente</strong>, lo que le da
        su nombre común.</li>
      <li><strong>Defensa:</strong> Posee glándulas parotoides (bultos tras los ojos) que secretan toxinas disuasorias
        si es atacado.</li>
    </ul>

    <h2>Reproducción en charcas efímeras</h2>
    <p>Estos anfibios son criaturas eminentemente terrestres, pero dependen del agua para reproducirse. Sienten
      preferencia por <strong>charcas temporales y poco profundas</strong>, ya que estas aguas efímeras suelen estar
      libres de depredadores como los peces.</p>
    <p>Durante las cálidas noches de primavera, los machos emiten un potente y característico canto (una especie de
      croar metálico y continuo) para atraer a las hembras. Tras el apareamiento, la hembra deposita largos cordones de
      huevos en el agua, de los que eclosionarán los renacuajos. Debido a que las charcas pueden secarse rápido, los
      renacuajos del sapito corredor tienen uno de los desarrollos más rápidos entre los anfibios europeos.</p>

    <h2>Importancia Ecológica del Sapito Corredor</h2>
    <p>El sapito corredor desempeña un papel crucial en la red trófica de los ecosistemas donde habita:</p>
    <ul>
      <li><strong>Control de plagas:</strong> Es un depredador nocturno voraz. Se alimenta de insectos, hormigas,
        escarabajos, arañas y otros invertebrados, ayudando a controlar las poblaciones naturales.</li>
      <li><strong>Fuente de alimento:</strong> Actúa como presa para numerosos animales superiores, incluyendo aves
        acuáticas, rapaces nocturnas, reptiles y pequeños mamíferos, ayudando así a mantener el equilibrio en el
        ecosistema.</li>
      <li><strong>Bioindicador:</strong> Su piel permeable y su doble ciclo de vida (acuático y terrestre) lo hacen
        extremadamente sensible a los cambios ambientales. Una población sana de sapitos es un indicador de que el
        ecosistema goza de buena salud.</li>
    </ul>

    <h2>Amenazas y Desafíos de Conservación</h2>
    <p>A pesar de su adaptabilidad, el <em>Bufo calamita</em> se enfrenta a un severo declive en sus poblaciones debido
      a:</p>
    <ul>
      <li><strong>Destrucción del hábitat:</strong> La urbanización, la agricultura intensiva y el drenaje de humedales
        eliminan sus zonas de cría.</li>
      <li><strong>Fragmentación:</strong> Las carreteras interrumpen sus rutas migratorias hacia las charcas de
        reproducción, provocando altas tasas de atropellos.</li>
      <li><strong>Contaminación:</strong> El uso de pesticidas y fertilizantes químicos contamina las aguas donde
        depositan sus huevos.</li>
      <li><strong>Cambio climático:</strong> Las sequías prolongadas secan las charcas efímeras antes de que los
        renacuajos puedan desarrollarse.</li>
    </ul>

    <div class="caja-recurso">
      <h3>Conecta con la fauna nocturna</h3>
      <p>
        La educación y la sensibilización pública juegan un papel crucial en la protección de estas especies
        vulnerables. En Geobizi organizamos salidas de observación responsable, como <strong>Naturgaua</strong>, donde
        (con suerte y respetando su entorno) podemos escuchar o descubrir a estos pequeños habitantes de la noche.
      </p>
      <div class="botones-recurso">
        <router-link to="/calendario" class="proximas-actividades">Ver próximas actividades</router-link>
        <router-link to="/contacto" class="btn-ver-mas">Contactar con nosotros</router-link>
      </div>
    </div>

    <p>Con esfuerzos conjuntos, promoviendo la creación de charcas y respetando su hábitat, podemos garantizar la
      supervivencia de este pequeño y veloz corredor ibérico.</p>

    <div class="imagen-trio">
      <div>
        <img class="img" src="/imagenes/blog/detalle/sapito1.avif"
          alt="Ejemplar adulto de sapito corredor (Epidalea calamita) mostrando su piel rugosa y la línea dorsal amarilla"
          title="Sapito corredor adulto" loading="lazy" />
      </div>
      <div>
        <img class="img" src="/imagenes/blog/detalle/sapito2.avif"
          alt="Detalle de primer plano de la cabeza de un sapito corredor, destacando sus ojos y glándulas"
          title="Detalle del sapito corredor" loading="lazy" />
      </div>
      <div>
        <img class="img" src="/imagenes/blog/detalle/sapito3.avif"
          alt="Charca poco profunda y vegetación, el hábitat reproductivo ideal para el sapito corredor"
          title="Hábitat reproductivo del sapito" loading="lazy" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { useHead } from '@vueuse/head'

// URL optimizada (coincide con el router)
const pageUrl = 'https://www.geobizi.com/blog/sapito-corredor'
const ogImage = 'https://www.geobizi.com/imagenes/blog/detalle/sapito1.avif'

useHead({
  // Título SEO
  title: 'El Sapito Corredor (Bufo calamita): Hábitat y Conservación | Geobizi',
  meta: [
    { name: 'description', content: 'Descubre todo sobre el Sapito Corredor (Epidalea calamita), un pequeño anfibio fascinante. Conoce su hábitat, amenazas y cómo Geobizi ayuda en su conservación.' },
    { name: 'robots', content: 'index, follow' },
    { name: 'author', content: 'Geobizi' },
    { name: 'theme-color', content: '#0b8a4c' },
    { name: 'language', content: 'es' },
    { property: 'og:title', content: 'Sapito Corredor: Un tesoro ecológico en peligro | Geobizi' },
    { property: 'og:description', content: 'Biología, características y conservación del sapito corredor. Aprende sobre este peculiar anfibio y la educación ambiental.' },
    { property: 'og:type', content: 'article' },
    { property: 'og:url', content: pageUrl },
    { property: 'og:image', content: ogImage },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:image', content: ogImage }
  ],
  link: [
    { rel: 'canonical', href: pageUrl },
    { rel: 'image_src', href: ogImage }
  ],
  script: [
    {
      type: 'application/ld+json',
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": "El Sapito Corredor (Bufo calamita): Un Tesoro Ecológico en Peligro",
        "description": "Información detallada sobre el sapito corredor (Epidalea calamita o Bufo calamita), su biología, importancia ecológica, amenazas y acciones de conservación.",
        "image": ogImage,
        "author": {
          "@type": "Organization",
          "name": "Geobizi",
          "url": "https://www.geobizi.com"
        },
        "publisher": {
          "@type": "Organization",
          "name": "Geobizi",
          "logo": {
            "@type": "ImageObject",
            "url": "https://www.geobizi.com/imagenes/GeobiziLogo.png"
          },
          // Redes sociales con TikTok
          "sameAs": [
            "https://www.facebook.com/geobizirik/",
            "https://www.instagram.com/geotxiki/",
            "https://www.tiktok.com/@geobizi",
            "https://www.youtube.com/channel/UCw-C_J0y-jKHp7Zx92lsKfg"
          ]
        },
        // Fechas requeridas para el Schema Article
        "datePublished": "2024-02-15T08:00:00+01:00",
        "dateModified": "2026-04-14T10:00:00+02:00",
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": pageUrl
        }
      })
    }
  ]
})
</script>

<style scoped>
/* Reutilizamos los estilos corporativos para la caja CTA y botones */
.caja-recurso {
  background-color: var(--megashoftgreen);
  border-left: 4px solid var(--shoftgreen);
  padding: 1.5rem;
  margin: 2.5rem 0;
  border-radius: 0 0.5rem 0.5rem 0;
}

.caja-recurso h3 {
  margin-top: 0;
  color: var(--lightgreen);
  font-size: 1.2rem;
}

.caja-recurso p {
  margin-bottom: 1.5rem;
  color: var(--darkgrey);
}

.botones-recurso {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.proximas-actividades {
  background-color: var(--supershoftgreen);
  color: var(--lightgreen);
  padding: 0.6rem 1.2rem;
  text-decoration: none;
  font-weight: bold;
  border-radius: 4px;
  transition: background-color 0.3s;
}

.proximas-actividades:hover {
  background-color: var(--shoftgreen);
  color: var(--white);
}

.btn-ver-mas {
  background-color: transparent;
  color: var(--lightgreen);
  padding: 0.6rem 1.2rem;
  text-decoration: none;
  font-weight: bold;
  border: 1px solid var(--shoftgreen);
  border-radius: 4px;
  transition: all 0.3s;
}

.btn-ver-mas:hover {
  background-color: var(--shoftgreen);
  color: white;
}

/* Este apartado es para las listas con hojitas */

/* Quitar los puntos por defecto de la lista */
.contenedor-principal ul {
  list-style-type: none;
  padding-left: 0;
}

/* Dar espacio a la izquierda para que quepa el icono */
.contenedor-principal li {
  position: relative;
  padding-left: 2rem;
  /* Espacio para las hojitas */
  margin-bottom: 0.8rem;
  line-height: 1.5;
}

/* Insertar la imagen de las hojitas */
.contenedor-principal li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 2px;
  width: 1.2rem;
  height: 1.2rem;

  /* AQUÍ ESTÁ EL CAMBIO MÁGICO: subimos dos carpetas (../../) */
  background-image: url('../../assets/Hojitas.png');

  background-size: contain;
  background-repeat: no-repeat;
}
</style>